import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { userAPI } from '../services/api';
import { showError, showSuccess } from '../utils/toast';
import { FaPaperPlane, FaTimes, FaUser, FaCar, FaClock } from 'react-icons/fa';

const DriverChat = ({ booking, onClose }) => {
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (booking?.id) {
      fetchChatHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchChatHistory = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getChatHistory(booking.id);
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      showError(t('chat.failed_to_load', 'Failed to load chat history'));
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);

    try {
      const chatData = {
        booking_id: booking.id,
        message: messageText,
        sender_type: 'user'
      };

      await userAPI.sendDriverMessage(chatData);
      
      // Add message to local state immediately for better UX
      const newMsg = {
        id: Date.now(), // Temporary ID
        message: messageText,
        sender_type: 'user',
        created_at: new Date().toISOString(),
        sender_name: 'You'
      };
      
      setMessages(prev => [...prev, newMsg]);
      showSuccess(t('chat.message_sent', 'Message sent to driver'));
      
      // Refresh chat history to get the actual message from server
      setTimeout(() => {
        fetchChatHistory();
      }, 1000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      showError(t('chat.failed_to_send', 'Failed to send message'));
      setNewMessage(messageText); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full h-[600px] flex flex-col">
        {/* Header */}
        <div className="bg-yellow-500 text-white p-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center">
            <FaCar className="mr-3" />
            <div>
              <h3 className="font-bold">{t('chat.chat_with_driver', 'Chat with Driver')}</h3>
              <p className="text-sm opacity-90">
                {booking?.driver_name || t('chat.assigned_driver', 'Assigned Driver')}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-xl p-1"
          >
            <FaTimes />
          </button>
        </div>

        {/* Booking Info */}
        <div className="bg-gray-50 p-3 border-b">
          <div className="text-sm text-gray-600">
            <p><strong>{t('chat.booking_id', 'Booking ID')}:</strong> #{booking?.id}</p>
            <p><strong>{t('chat.route', 'Route')}:</strong> {booking?.pickup_location} → {booking?.dropoff_location}</p>
            <p><strong>{t('chat.date', 'Date')}:</strong> {booking?.pickup_date}</p>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-yellow-400 border-t-transparent"></div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-gray-500">
              <FaCar className="text-4xl mb-2 opacity-50" />
              <p>{t('chat.no_messages', 'No messages yet')}</p>
              <p className="text-sm">{t('chat.start_conversation', 'Start a conversation with your driver')}</p>
            </div>
          ) : (
            messages.map((message, index) => {
              const isUser = message.sender_type === 'user';
              const showDate = index === 0 || 
                formatDate(messages[index - 1].created_at) !== formatDate(message.created_at);

              return (
                <div key={message.id || index}>
                  {showDate && (
                    <div className="text-center text-xs text-gray-500 my-2">
                      {formatDate(message.created_at)}
                    </div>
                  )}
                  <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                      isUser 
                        ? 'bg-yellow-500 text-white' 
                        : 'bg-gray-200 text-gray-800'
                    }`}>
                      <div className="flex items-center mb-1">
                        {isUser ? <FaUser className="mr-2 text-xs" /> : <FaCar className="mr-2 text-xs" />}
                        <span className="text-xs font-medium">
                          {isUser ? t('chat.you', 'You') : (message.sender_name || t('chat.driver', 'Driver'))}
                        </span>
                      </div>
                      <p className="text-sm">{message.message}</p>
                      <div className="flex items-center justify-end mt-1">
                        <FaClock className="mr-1 text-xs opacity-70" />
                        <span className="text-xs opacity-70">
                          {formatTime(message.created_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="border-t p-4">
          <div className="flex items-center space-x-2">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chat.type_message', 'Type your message...')}
              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-400 focus:border-transparent resize-none"
              rows="2"
              disabled={sending}
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || sending}
              className="bg-yellow-500 text-white p-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? (
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
              ) : (
                <FaPaperPlane />
              )}
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {t('chat.press_enter', 'Press Enter to send, Shift+Enter for new line')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default DriverChat;
